// Masked input for API keys, tokens and app passwords in admin settings.
//
// Browsers treat any type="password" field as a login password and ignore
// autoComplete="off" on it: Chrome filled the agent's own sign-in password into
// the Mailchimp key field, which the server then rejected as "not a Mailchimp
// key". "new-password" stops the browser filling a saved login, and the data-*
// attributes do the same for 1Password, LastPass and Bitwarden. The eye button
// shows what is actually in the field.

import { useState, type CSSProperties, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import styles from './admin.module.css';

type SecretInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'autoComplete'> & {
  /** Style for the wrapper, e.g. `{ flex: 1 }` when the input shares a row. */
  wrapperStyle?: CSSProperties;
};

export function SecretInput({ wrapperStyle, className, ...props }: SecretInputProps) {
  const [shown, setShown] = useState(false);
  return (
    <span className={styles.passwordInputWrap} style={wrapperStyle}>
      <input
        {...props}
        className={[styles.passwordInput, className].filter(Boolean).join(' ')}
        type={shown ? 'text' : 'password'}
        autoComplete="new-password"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        data-1p-ignore=""
        data-lpignore="true"
        data-bwignore=""
        data-form-type="other"
      />
      <button
        type="button"
        className={styles.passwordToggleBtn}
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? 'Hide value' : 'Show value'}
        aria-pressed={shown}
        title={shown ? 'Hide' : 'Show'}
        disabled={props.disabled}
      >
        {shown ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
      </button>
    </span>
  );
}
